import type { ChannelMask } from '../hooks/useChannelsForSettings'

export type ChannelRefreshNoticeType =
  | 'xMigration'
  | 'linkedinUpgrade'
  | 'pinterestUpgrade'
  | 'tiktokUpgrade'
  | 'igLoginRefresh'

export type ChannelRefreshNotice = {
  type: ChannelRefreshNoticeType
  channels: ChannelMask[]
}

type Options = {
  isIgLoginRefreshEnabled: boolean
}

const hasBanner = (
  channel: ChannelMask,
  banner: NonNullable<ChannelMask['banners']>[number],
): boolean => channel.banners?.includes(banner) ?? false

/**
 * Notice types ordered by severity; each channel is assigned to the first
 * matching type only, so a channel never appears in two notices at once.
 */
const noticeMatchers: Array<{
  type: ChannelRefreshNoticeType
  matches: (channel: ChannelMask, options: Options) => boolean
}> = [
  {
    type: 'xMigration',
    matches: (channel) =>
      channel.service === 'twitter' && channel.apiVersion !== '2',
  },
  {
    type: 'linkedinUpgrade',
    matches: (channel) => hasBanner(channel, 'linkedinUpgrade'),
  },
  {
    type: 'pinterestUpgrade',
    matches: (channel) => hasBanner(channel, 'pinterestUpgrade'),
  },
  {
    type: 'tiktokUpgrade',
    matches: (channel) => hasBanner(channel, 'tiktokUpgrade'),
  },
  {
    type: 'igLoginRefresh',
    matches: (channel, options) =>
      options.isIgLoginRefreshEnabled && hasBanner(channel, 'igLoginRefresh'),
  },
]

/**
 * Groups channels needing a connection refresh into the notices shown at the
 * top of the Settings → Channels page, ordered by severity.
 */
export const getChannelRefreshNotices = (
  channels: ChannelMask[],
  options: Options,
): ChannelRefreshNotice[] => {
  const notices: ChannelRefreshNotice[] = noticeMatchers.map(({ type }) => ({
    type,
    channels: [],
  }))

  for (const channel of channels) {
    if (channel.isLocked) {
      continue
    }

    const matchedIndex = noticeMatchers.findIndex(({ matches }) =>
      matches(channel, options),
    )
    if (matchedIndex !== -1) {
      notices[matchedIndex].channels.push(channel)
    }
  }

  return notices.filter((notice) => notice.channels.length > 0)
}
